import { FC, memo, ReactNode, useMemo } from 'react';
import { Box, keyframes, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { removeHighlight } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CertMember } from 'src/types/Certification';
import { formatPhoneNumber } from 'src/utils/InputValidationUtils';
import { getRoleColors, isMinorMemberType } from '../memberRoleColors';

interface HouseholdCompositionMemberCardProps {
  member: CertMember;
  isRecentlyAdded?: boolean;
  hasDuplicateContact?: boolean;
  canManage?: boolean;
  currentUserId?: number;
  onClick?: (member: CertMember) => void;
  onEdit?: (member: CertMember) => void;
  onDelete?: (member: CertMember) => void;
}

const HouseholdMemberCard: FC<HouseholdCompositionMemberCardProps> = ({
  member,
  isRecentlyAdded = false,
  hasDuplicateContact = false,
  canManage = true,
  currentUserId,
  onEdit,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const blueGlow = useMemo(
    () =>
      keyframes`
        0%, 100% {
          box-shadow: 0 0 0 0 ${alpha(theme.palette.info.main, 0)};
        }
        50% {
          box-shadow: 0 0 20px 4px ${alpha(theme.palette.info.main, 0.6)};
        }
      `,
    [theme.palette.info.main]
  );

  const canInteractWithCard =
    canManage || (currentUserId != null && member.user_id === currentUserId);

  const handleAnimationEnd = (): void => {
    if (isRecentlyAdded) {
      dispatch(removeHighlight(member.cert_member_id));
    }
  };

  const getMemberFullName = (member: CertMember): string => {
    const parts = [
      member.first_name,
      member.middle_name,
      member.last_name,
    ].filter(Boolean);
    return parts.join(' ').trim() || '—';
  };

  const getContactEntries = (
    member: CertMember
  ): Array<{ icon: ReactNode; text: string }> => {
    const primaryEmail = member.emails?.find(
      (e) => e.is_primary && !e.email.endsWith('@prontohousing.io')
    );
    const primaryPhone = member.phones?.find((p) => p.is_primary);
    const entries: Array<{ icon: ReactNode; text: string }> = [];
    if (primaryEmail?.email) {
      entries.push({
        icon: <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        text: primaryEmail.email,
      });
    }
    if (primaryPhone?.phone_number) {
      const digits = primaryPhone.phone_number.replace(/\D/g, '');
      const normalized =
        digits.length === 11 && digits.startsWith('1')
          ? digits.slice(1)
          : digits;
      entries.push({
        icon: <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        text: formatPhoneNumber(normalized),
      });
    }
    if (entries.length === 0) {
      entries.push({
        icon: (
          <WarningAmberIcon fontSize="small" sx={{ color: 'warning.dark' }} />
        ),
        text: 'Please Add Contact Details',
      });
    }
    return entries;
  };

  const colors = getRoleColors(member.member_type_name);
  const isMinor = isMinorMemberType(member.member_type_name);
  const initials = [member.first_name, member.last_name]
    .filter((n): n is string => Boolean(n))
    .map((n) => n[0].toUpperCase())
    .join('');
  const contactEntries = isMinor ? [] : getContactEntries(member);
  const isTappable = canInteractWithCard && Boolean(onEdit);

  return (
    <Stack
      gap={1.5}
      onAnimationEnd={handleAnimationEnd}
      onClick={isTappable && onEdit ? () => onEdit(member) : undefined}
      sx={{
        height: '100%',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        borderTop: 1,
        borderRight: 1,
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: colors.accentColor,
        py: 2,
        px: 2,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0px 2px 8px rgba(0,0,0,0.24)'
            : `0px 2px 8px ${alpha(theme.palette.text.secondary, 0.12)}`,
        animation: isRecentlyAdded ? `${blueGlow} 3s ease-in-out 2` : 'none',
        transition: 'border-color 0.3s ease-in-out',
        ...(isTappable && {
          cursor: 'pointer',
          '&:hover': { borderLeftColor: colors.accentColor, boxShadow: 2 },
        }),
      }}
    >
      {/* Top row: avatar + name/pill + chevron */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: colors.avatarBg,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: colors.avatarText, fontSize: '1rem' }}
            >
              {initials || '?'}
            </Typography>
          </Box>

          <Stack gap={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600}>
              {getMemberFullName(member)}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: colors.pillBg,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: colors.pillText,
                }}
              >
                {member.member_type_name}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {isTappable && (
          <ChevronRightIcon
            sx={(theme) => ({
              color:
                theme.palette.mode === 'dark' ? 'common.white' : 'grey.900',
              flexShrink: 0,
            })}
          />
        )}
      </Stack>

      {/* Contact rows — omitted entirely for minor/dependent members */}
      {contactEntries.length > 0 && (
        <Stack gap={0.75}>
          {contactEntries.map((entry, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ minWidth: 0 }}
            >
              {entry.icon}
              <Typography
                variant="body2"
                color={
                  entry.text === 'Please Add Contact Details'
                    ? 'warning.dark'
                    : 'text.secondary'
                }
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Duplicate-contact warning */}
      {hasDuplicateContact && (
        <Stack direction="row" alignItems="center" gap={1}>
          <WarningAmberIcon fontSize="small" sx={{ color: 'warning.dark' }} />
          <Typography variant="caption" color="warning.dark" fontWeight={600}>
            Duplicate contact with another household member
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(HouseholdMemberCard);
